// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/growth-score/pages/BrandPage.tsx (`BrandProfileCard`, `Field`, `OptionChip`)
// ADAPTED (plan K6, P3-10): TP's card, chips and "Other…" row, filled from the brand guide the BFF read instead of
// fixtures. TP's age bands, "% of Match" weights and Back are not ported (no Match model yet; no Back in the chat).
// Customer, positioning and tone are read-only text from the guide. What is confirmed is kept in the browser session.
import { ArrowRight, Pencil, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { BrandLogo } from '@/components/ui/brand-logo';
import { Button } from '@/components/ui/shadcn/button';
import { Card } from '@/components/ui/shadcn/card';
import { selectionSurface } from '@/components/ui/selection';
import { Spinner } from '@/components/ui/spinner';
import type { BrandGuideResponse } from '@/contracts/onboarding-session';
import { cn } from '@/lib/utils';

import { BRAND_PROFILE, brandProfileSource, COULDNT_READ, STILL_READING } from '../copy';
import { PANEL_CLICK_GUARD_MS, useClickGuard } from '../hooks/useClickGuard';
import { BRAND_PROFILE_LIMITS, DEFAULT_SELECTED_PRODUCTS, PRODUCT_CHIPS_SHOWN } from '../constants';
import { prospectLogoUrl } from '../state/researchSteps';
import type { BrandProfile } from '../state/session';

/** The products the guide names, once each (case-insensitive): the research's top sellers first, then the site's own list. */
export function listedProducts(guide: BrandGuideResponse | null): string[] {
  const names = [
    ...(guide?.topSellers?.value ?? []).map((s) => s.name),
    ...(guide?.products?.value ?? []).map((p) => p.name),
  ];
  const seen = new Set<string>();
  const listed: string[] = [];
  for (const raw of names) {
    const name = raw.trim().slice(0, BRAND_PROFILE_LIMITS.productLength);
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    listed.push(name);
  }
  return listed.slice(0, PRODUCT_CHIPS_SHOWN);
}

const textOf = (value: string | null | undefined): string | null => (value?.trim() ? value.trim() : null);

/** The customer, positioning and tone the guide holds, as the text the card shows (null = not read). */
export function profileTexts(guide: BrandGuideResponse | null): { customer: string | null; positioning: string | null; tone: string | null } {
  const tone = (guide?.tone?.value ?? []).map((t) => t.adjective.trim()).filter(Boolean);
  return {
    customer: textOf(guide?.icp?.value),
    positioning: textOf(guide?.positioning?.value),
    tone: tone.length > 0 ? tone.join(', ') : null,
  };
}

/** One part of the profile: a group named by its own visible label. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  const labelId = useId();
  return (
    <div className="flex flex-col gap-2 rounded-md border border-ds-hairline p-3" role="group" aria-labelledby={labelId}>
      <span id={labelId} className="text-caption text-ds-text-2">
        {label}
      </span>
      {children}
    </div>
  );
}

/** A pressable option: chosen ones sit on 10% ink, as options inside a card do. */
function OptionChip({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        // TP's chip is `h-7`; a product name off a real site can be a whole line long, so the chip is `min-h-7` and wraps (it
        // never overprints).
        'inline-flex min-h-7 max-w-full items-center gap-1 border border-ds-hairline px-2.5 py-0.5 text-left text-tab focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus',
        selectionSurface({ level: 'card-tab', selected }),
      )}
    >
      {children}
    </button>
  );
}

const Reading = () => (
  <span data-testid="profile-reading" className="inline-flex items-center gap-1.5 text-body-sm text-ds-text-2">
    <Spinner size="sm" />
    <span className="onb-shimmer">{BRAND_PROFILE.reading}</span>
  </span>
);

const Pending = () => (
  <span data-testid="profile-pending" className="inline-flex items-center gap-1.5 text-body-sm text-ds-text-2">
    <Spinner size="sm" />
    <span className="onb-shimmer">{STILL_READING}</span>
  </span>
);

const Missing = () => (
  <span data-source="missing" className="text-body-sm text-ds-text-3">
    {COULDNT_READ}
  </span>
);

export interface BrandProfileCardProps {
  brand: string;
  /** The site the guide was built from. */
  site?: string;
  guide: BrandGuideResponse | null;
  /** The brand guide is being read for the first time: the fields read "Reading your brand…" and Confirm waits. */
  loading: boolean;
  /** The brand guide could not be read at all: every field says so, and Confirm works once the research is over. */
  failed: boolean;
  /**
   * The research (CI) job is still going: a field only it fills reads "Still reading…" instead of "Couldn't read",
   * and Confirm waits while customer, positioning or tone is still empty (confirming would store them as unknown
   * before the research had a chance to read them).
   */
  researchPending?: boolean;
  onConfirm: (profile: Omit<BrandProfile, 'confirmedAt'>) => void;
  /** Clicks are ignored this long after the card mounts (the step before it had a button under the same pointer). */
  clickGuardMs?: number;
}

/**
 * The profile the Match compares, to confirm before the competitors are looked for. Products are chips (the first
 * few selected), with an "Other…" row for the ones the site did not list; customer, positioning and tone are read
 * from the brand guide. Confirm is possible once the guide has loaded (or failed) and the research has either filled
 * customer, positioning and tone or ended: what it could not read is then "Couldn't read" and is stored as unknown.
 */
export function BrandProfileCard({
  brand,
  site,
  guide,
  loading,
  failed,
  researchPending = false,
  onConfirm,
  clickGuardMs = PANEL_CLICK_GUARD_MS,
}: BrandProfileCardProps) {
  const guard = useClickGuard(clickGuardMs);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // The card mounts for the step, so mounting is "a new step": a screen reader hears it.
  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  const reading = loading && !guide && !failed;
  const listed = useMemo(() => listedProducts(guide), [guide]);
  const texts = profileTexts(guide);
  // null = untouched: the first few listed are selected, and follow the list as the guide fills in.
  const [picked, setPicked] = useState<string[] | null>(null);
  const [custom, setCustom] = useState<string[]>([]);
  const [other, setOther] = useState('');
  const selected = picked ?? listed.slice(0, DEFAULT_SELECTED_PRODUCTS);
  // The research may still be reading what is empty: Confirm waits for it, with the reason in words.
  const awaitingResearch = researchPending && (texts.customer === null || texts.positioning === null || texts.tone === null);
  const hintId = useId();

  const toggleListed = (name: string) =>
    setPicked(selected.includes(name) ? selected.filter((p) => p !== name) : [...selected, name]);
  const removeCustom = (name: string) => setCustom((prev) => prev.filter((p) => p !== name));
  const addOther = (event: FormEvent) => {
    event.preventDefault();
    const label = other.trim().slice(0, BRAND_PROFILE_LIMITS.productLength);
    if (!label) return;
    const key = label.toLowerCase();
    // Typing the name of a listed product picks that product instead of duplicating it.
    const known = listed.find((p) => p.toLowerCase() === key);
    if (known) {
      if (!selected.includes(known)) setPicked([...selected, known]);
    } else if (!custom.some((p) => p.toLowerCase() === key)) {
      setCustom((prev) => [...prev, label]);
    }
    setOther('');
  };

  const confirm = guard(() => {
    onConfirm({
      products: [...listed.filter((p) => selected.includes(p)), ...custom],
      customer: texts.customer,
      positioning: texts.positioning,
      tone: texts.tone,
    });
  });

  /** A read-only field: the text, "Reading your brand…" while the guide loads, "Still reading…" while research does, else "Couldn't read". */
  const readOnly = (text: string | null, tone: 'body' | 'label' = 'body') =>
    text ? (
      <p className={tone === 'label' ? 'text-label text-ds-text-2' : 'text-body-sm text-foreground'}>{text}</p>
    ) : reading ? (
      <Reading />
    ) : researchPending ? (
      <Pending />
    ) : (
      <Missing />
    );

  return (
    <Card role="group" aria-label={BRAND_PROFILE.groupLabel} data-testid="brand-profile-card" className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-2.5">
        <BrandLogo name={brand} src={prospectLogoUrl(guide)} className="size-6" />
        <h3
          ref={headingRef}
          tabIndex={-1}
          className="rounded-sm text-heading-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus"
        >
          {brand}
        </h3>
        <span className="min-w-0 truncate text-label text-ds-text-2">{brandProfileSource(site)}</span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Field label={BRAND_PROFILE.products}>
          {reading ? (
            <Reading />
          ) : listed.length === 0 && custom.length === 0 ? (
            researchPending ? <Pending /> : <Missing />
          ) : null}
          <div className="flex flex-wrap gap-1.5">
            {listed.map((name) => (
              <OptionChip key={name} selected={selected.includes(name)} onClick={() => toggleListed(name)}>
                {name}
              </OptionChip>
            ))}
            {custom.map((name) => (
              <OptionChip key={name} selected onClick={() => removeCustom(name)}>
                {name}
                <X aria-hidden className="size-3.5" />
                <span className="sr-only">(remove)</span>
              </OptionChip>
            ))}
          </div>
          <form onSubmit={addOther} className="flex h-7 items-center gap-1.5 rounded-md border border-ds-hairline px-2 focus-within:border-ds-focus">
            <Pencil aria-hidden className="size-3.5 shrink-0 text-ds-text-3" />
            <input
              value={other}
              onChange={(event) => setOther(event.target.value)}
              placeholder={BRAND_PROFILE.otherPlaceholder}
              aria-label={BRAND_PROFILE.otherLabel}
              maxLength={BRAND_PROFILE_LIMITS.productLength}
              // The DS draws a 2px outline on every :focus-visible (specificity above `outline-none`): the row's own border is the focus mark.
              className="min-w-0 flex-1 bg-transparent text-body-sm text-foreground outline-none placeholder:text-ds-text-3 focus-visible:!outline-none"
            />
          </form>
        </Field>
        <Field label={BRAND_PROFILE.customer}>{readOnly(texts.customer, 'label')}</Field>
        <Field label={BRAND_PROFILE.positioning}>{readOnly(texts.positioning)}</Field>
        <Field label={BRAND_PROFILE.tone}>{readOnly(texts.tone)}</Field>
      </div>

      <div className="flex items-center justify-end gap-3">
        {awaitingResearch ? (
          <span id={hintId} data-testid="profile-confirm-hint" className="text-label text-ds-text-2">
            {BRAND_PROFILE.stillReadingHint}
          </span>
        ) : null}
        <Button type="button" disabled={reading || awaitingResearch} aria-describedby={awaitingResearch ? hintId : undefined} onClick={confirm}>
          {BRAND_PROFILE.confirm}
          <ArrowRight aria-hidden />
        </Button>
      </div>
    </Card>
  );
}
